import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useEffect, useState } from "react";
import { useDisableHotkeyScopes } from "../hooks/useDisableHotkeyScopes";
import { usePauseVideoWhileOpen } from "../hooks/usePauseVideoWhileOpen";
import { MaterialRead } from "src/store/api/generatedApi";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { MaterialHotkeyOverlay } from "components/scrapEyeLayout/hotkeys/MaterialHotkeyOverlay";
import { useWaitForKeyInput } from "../hooks/useWaitForKeyInput";
import { getLabellingMaterialDisplayName } from "../utils/materialDisplayName";
import { LABELLING_HOTKEY_SCOPES } from "components/manualReview/hotkeyScopes";

type MaterialHotkeyConfigModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  materials: readonly MaterialRead[];
  disableScopes?: readonly string[];
}>;

export const MaterialHotkeyConfigModal = ({
  open,
  onClose,
  materials,
  disableScopes = LABELLING_HOTKEY_SCOPES,
}: MaterialHotkeyConfigModalProps) => {
  const { t } = useTenantTranslation();
  const { getMaterialHotkey, setMaterialHotkey, resetMaterialHotkey } =
    useHotkeyContext();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useDisableHotkeyScopes(disableScopes, open);
  usePauseVideoWhileOpen(open);

  const findMaterialWithHotkey = useCallback(
    (targetCode: string): number | null => {
      if (getMaterialHotkey(-1) === targetCode) {
        return -1;
      }
      for (let i = 0; i < materials.length; i++) {
        if (getMaterialHotkey(i) === targetCode) {
          return i;
        }
      }
      return null;
    },
    [materials.length, getMaterialHotkey]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (selectedIndex === null) return false;

      const code = event.code;
      if (
        !code.startsWith("Key") &&
        !code.startsWith("Digit") &&
        code !== "Comma" &&
        code !== "Period" &&
        code !== "Semicolon" &&
        code !== "Slash"
      ) {
        return false;
      }

      const existingMaterialIndex = findMaterialWithHotkey(code);
      const oldHotkey = getMaterialHotkey(selectedIndex);

      if (
        existingMaterialIndex !== null &&
        existingMaterialIndex !== selectedIndex
      ) {
        // Swap: give the other material our old hotkey
        setMaterialHotkey(existingMaterialIndex, oldHotkey);
      }

      // Assign the new hotkey to the selected material
      setMaterialHotkey(selectedIndex, code);
      setSelectedIndex(null);
    },
    [
      selectedIndex,
      findMaterialWithHotkey,
      getMaterialHotkey,
      setMaterialHotkey,
    ]
  );

  const [waitingForInput, startWaiting, stopWaiting] =
    useWaitForKeyInput(handleKeyPress);

  const handleKeyClick = useCallback(
    (materialIndex: number) => {
      setSelectedIndex(materialIndex);
      startWaiting();
    },
    [startWaiting]
  );

  const handleResetMaterial = useCallback(
    (index: number) => {
      resetMaterialHotkey(index);
      setSelectedIndex(null);
      stopWaiting();
    },
    [resetMaterialHotkey, stopWaiting]
  );

  const handleResetAll = useCallback(() => {
    for (let i = -1; i < materials.length; i++) {
      resetMaterialHotkey(i);
    }
    setSelectedIndex(null);
    stopWaiting();
  }, [materials.length, resetMaterialHotkey, stopWaiting]);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- bidirectional state reset on dialog close
      setSelectedIndex(null);
      stopWaiting();
    }
  }, [open, stopWaiting]);

  const getDisplayKeyFromCode = (code: string): string => {
    if (code.startsWith("Key")) return code.slice(3);
    if (code.startsWith("Digit")) return code.slice(5);
    if (code === "Comma") return ",";
    if (code === "Period") return ".";
    if (code === "Semicolon") return ";";
    if (code === "Slash") return "/";
    return code;
  };

  const materialDisplayName = getLabellingMaterialDisplayName(
    selectedIndex === -1 || selectedIndex === null
      ? null
      : (materials[selectedIndex] ?? null),
    t
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <span>{t("configureMaterialHotkeys")}</span>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="text"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleResetAll}
            >
              {t("resetAll")}
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            {waitingForInput && selectedIndex !== null
              ? t("pressKeyForMaterial", {
                  material: materialDisplayName,
                })
              : t("clickMaterialToChangeHotkey")}
          </Typography>

          <MaterialHotkeyOverlay
            materials={materials}
            onMaterialClick={handleKeyClick}
            selectedMaterialIndex={selectedIndex}
            isWaitingForInput={waitingForInput}
            onUnknownClick={() => {
              handleKeyClick(-1);
            }}
          />

          {selectedIndex !== null ? (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="body2">
                {materialDisplayName}:{" "}
                <strong>
                  {getDisplayKeyFromCode(getMaterialHotkey(selectedIndex))}
                </strong>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  handleResetMaterial(selectedIndex);
                }}
              >
                {t("resetToDefault")}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
